import type { Continent, ExplorerSection, WorldObject } from '@/types'
import {
  CONTINENT_NAMES_ZH_HANS,
  CONTINENT_NAMES_ZH_HANT,
  COUNTRY_NAMES_ZH_HANS,
  COUNTRY_NAMES_ZH_HANT,
} from '@/data/countryNames'
import type { Locale } from './languageStore'
import { t, type TranslationKey } from './i18n'

function translationFor(obj: WorldObject, locale: Locale) {
  if (locale === 'zh-Hans') return obj.translations?.zhHans
  if (locale === 'zh-Hant') return obj.translations?.zhHant
  return undefined
}

/** Falls back to the canonical English field whenever the locale is 'en', or a translation is missing/incomplete. */
export function getLocalizedName(obj: WorldObject, locale: Locale): string {
  return translationFor(obj, locale)?.name ?? obj.name
}

export function getLocalizedTagline(obj: WorldObject, locale: Locale): string {
  return translationFor(obj, locale)?.tagline ?? obj.tagline
}

export function getLocalizedDescription(obj: WorldObject, locale: Locale): string {
  return translationFor(obj, locale)?.description ?? obj.description
}

export function getLocalizedFunFacts(obj: WorldObject, locale: Locale): string[] {
  return translationFor(obj, locale)?.funFacts ?? obj.funFacts
}

export function getLocalizedDidYouKnow(obj: WorldObject, locale: Locale): string[] {
  return translationFor(obj, locale)?.didYouKnow ?? obj.didYouKnow
}

/**
 * Merges translated title/body onto the English `sections` array by
 * index — `emoji` always comes from the English section since it isn't
 * translated. A missing translated entry at a given index (partial
 * translation, or a translation that hasn't caught up with a later
 * content edit) falls back to that section's English text individually,
 * rather than discarding the whole array.
 */
export function getLocalizedSections(obj: WorldObject, locale: Locale): ExplorerSection[] {
  const translated = translationFor(obj, locale)?.sections
  if (!translated) return obj.sections
  return obj.sections.map((section, i) => ({
    emoji: section.emoji,
    title: translated[i]?.title ?? section.title,
    body: translated[i]?.body ?? section.body,
  }))
}

/**
 * `country`/`continent` themselves stay canonical English strings (they're
 * matching keys for COUNTRY_BADGES/search/isCompletionBadgeEarned, not
 * display text) — these two helpers only translate what's *shown*,
 * falling back to the English value for any country not yet in the
 * dictionary (e.g. a newly-added place before countryNames.ts catches up).
 */
export function getLocalizedCountryName(country: string, locale: Locale): string {
  if (locale === 'zh-Hans') return COUNTRY_NAMES_ZH_HANS[country] ?? country
  if (locale === 'zh-Hant') return COUNTRY_NAMES_ZH_HANT[country] ?? country
  return country
}

export function getLocalizedContinentName(continent: Continent, locale: Locale): string {
  if (locale === 'zh-Hans') return CONTINENT_NAMES_ZH_HANS[continent] ?? continent
  if (locale === 'zh-Hant') return CONTINENT_NAMES_ZH_HANT[continent] ?? continent
  return continent
}

/**
 * Text to *speak*, as opposed to text to *display*. Pre-generated Chinese
 * audio clips (scripts/generate-audio.mjs) are only ever synthesized from
 * each place's zhHans translation — never zhHant — so both Chinese display
 * locales share one clip per sentence instead of needing two. These helpers
 * resolve to zhHans specifically even when the display locale is zh-Hant,
 * falling back to the canonical English field when the locale is 'en' or no
 * zhHans translation exists yet for this place (same graceful-fallback
 * pattern as the display helpers above, just pinned to zhHans instead of
 * branching on locale).
 */
function speechTranslationFor(obj: WorldObject, locale: Locale) {
  return locale === 'en' ? undefined : obj.translations?.zhHans
}

/** The locale to pass to `t()` when building a *spoken* (not displayed) UI template — always zhHans for either Chinese locale, since that's the only script audio clips exist for. */
export function toSpeechLocale(locale: Locale): Locale {
  return locale === 'en' ? 'en' : 'zh-Hans'
}

export function getSpeechName(obj: WorldObject, locale: Locale): string {
  return speechTranslationFor(obj, locale)?.name ?? obj.name
}

export function getSpeechTagline(obj: WorldObject, locale: Locale): string {
  return speechTranslationFor(obj, locale)?.tagline ?? obj.tagline
}

export function getSpeechDescription(obj: WorldObject, locale: Locale): string {
  return speechTranslationFor(obj, locale)?.description ?? obj.description
}

export function getSpeechFunFacts(obj: WorldObject, locale: Locale): string[] {
  return speechTranslationFor(obj, locale)?.funFacts ?? obj.funFacts
}

export function getSpeechDidYouKnow(obj: WorldObject, locale: Locale): string[] {
  return speechTranslationFor(obj, locale)?.didYouKnow ?? obj.didYouKnow
}

export function getSpeechSections(obj: WorldObject, locale: Locale): ExplorerSection[] {
  const translated = speechTranslationFor(obj, locale)?.sections
  if (!translated) return obj.sections
  return obj.sections.map((section, i) => ({
    emoji: section.emoji,
    title: translated[i]?.title ?? section.title,
    body: translated[i]?.body ?? section.body,
  }))
}

const CJK_PATTERN = /[一-鿿]/

/**
 * Builds the exact string spoken for "read this whole section aloud" (title
 * + every paragraph) — must stay byte-for-byte identical to what
 * scripts/generate-audio.mjs composes for the matching pre-generated clip,
 * since both hash the exact text to find it. Chinese doesn't use an
 * English-style ". " separator or space-joined paragraphs, so a section
 * spoken in Chinese needs its own template — detected from the section's
 * own text (not the display locale) so English-fallback text for a
 * not-yet-translated place still composes correctly too.
 */
export function composeSectionSpeech(section: { title: string; body: string[] }): string {
  if (CJK_PATTERN.test(section.title)) return `${section.title}。${section.body.join('')}`
  return `${section.title}. ${section.body.join(' ')}`
}

/**
 * Search/gallery filter chips (continent/theme/type) — continent chips
 * reuse the country/continent dictionary above; theme/type chips look up
 * `filterTheme.<value>`/`filterType.<value>` in the main i18n dictionary.
 * Falls back to the filter's own English `label` if a value isn't in the
 * dictionary yet, same graceful-degradation pattern as everything else here.
 */
export function getLocalizedFilterLabel(
  filter: { kind: 'continent' | 'theme' | 'type'; value: string; label: string },
  locale: Locale,
): string {
  if (locale === 'en') return filter.label
  if (filter.kind === 'continent') return getLocalizedContinentName(filter.value as Continent, locale)
  const key = `filter${filter.kind === 'theme' ? 'Theme' : 'Type'}.${filter.value}` as TranslationKey
  const entry = t(key, locale)
  return entry === key ? filter.label : entry
}
